import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Search, X } from 'lucide-react'
import { chatApi } from '../../api/chat'
import { displayName } from '../../utils/nameHelpers'
import { roleLabel } from '../../utils/roleLabels'

/** Инициалы для аватара: «Иванов Иван» → «ИИ». */
function initialsOf(fullName) {
    if (!fullName) return '?'
    const parts = fullName.trim().split(/\s+/).slice(0, 2)
    return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?'
}

/** Стабильный hue из id для цвета аватара (HSL, 60° шаг). */
function avatarHue(id) {
    const s = String(id)
    let h = 0
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360
    return h
}


export default function NewChatDialog({
    chat,
    canCreateChannel = false,
    initialTab = 'direct',
    onClose,
    onRoomCreated,
}) {
    const [tab, setTab] = useState(initialTab)
    const [groupName, setGroupName] = useState('')
    const [selectedUserIds, setSelectedUserIds] = useState(() => new Set())
    const [userQuery, setUserQuery] = useState('')
    const [userHits, setUserHits] = useState([])
    const [searching, setSearching] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => { setTab(initialTab) }, [initialTab])

    const searchDebounced = useCallback(async (q) => {
        setSearching(true)
        try {
            const rows = await chatApi.searchUsers(q)
            setUserHits(rows || [])
        } catch {
            setUserHits([])
        } finally {
            setSearching(false)
        }
    }, [])

    useEffect(() => {
        const t = setTimeout(() => searchDebounced(userQuery), 250)
        return () => clearTimeout(t)
    }, [userQuery, searchDebounced])

    const toggleSelectUser = (id) => {
        setSelectedUserIds((prev) => {
            const n = new Set(prev)
            const sid = String(id)
            if (n.has(sid)) n.delete(sid)
            else n.add(sid)
            return n
        })
    }

    const handleStartDirect = async (other) => {
        setError(null)
        try {
            const room = await chat.createDirectChat(other.id)
            if (room?.id) onRoomCreated?.(room)
            else setError('Сервер не вернул данные комнаты. Повторите попытку.')
        } catch (e) {
            setError(e?.message || 'Не удалось создать чат')
        }
    }

    const submitGroupOrChannel = async () => {
        if (!groupName.trim()) return
        const roomType = tab === 'channel' ? 'channel' : 'group'
        const room = await chat.createGroupRoom({
            name: groupName.trim(),
            participant_ids: Array.from(selectedUserIds),
            room_type: roomType,
        })
        if (room?.id) onRoomCreated?.(room)
    }

    return (
        <div
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-chat-title"
            onClick={onClose}
            // ChatPanel drawer z-index = var(--z-toast) = 1200. Глобальный
            // .modal-overlay = 200 (legacy) → модалка уходила под мессенджер
            // и пользователь не мог выбрать сотрудников. Поднимаем выше
            // chat-panel, но ниже toast-уведомлений (~1500).
            style={{ zIndex: 1250 }}
        >
            <div className="modal-panel" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 id="new-chat-title" className="modal-header__title">
                        {tab === 'direct' ? 'Личный чат' : tab === 'group' ? 'Групповой чат' : 'Новый канал'}
                    </h2>
                    <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button>
                </div>
                <div className="modal-body">
                    <div className="tab-bar mb-16">
                        <button type="button" className={`tab-btn${tab === 'direct' ? ' tab-btn--active' : ''}`} onClick={() => setTab('direct')}>Личный</button>
                        <button type="button" className={`tab-btn${tab === 'group' ? ' tab-btn--active' : ''}`} onClick={() => setTab('group')}>Группа</button>
                        {canCreateChannel && (
                            <button type="button" className={`tab-btn${tab === 'channel' ? ' tab-btn--active' : ''}`} onClick={() => setTab('channel')}>Канал</button>
                        )}
                    </div>

                    {(tab === 'group' || tab === 'channel') && (
                        <div className="form-field">
                            <label className="form-label">{tab === 'channel' ? 'Название канала' : 'Название группы'}</label>
                            <input
                                className="form-input"
                                value={groupName}
                                onChange={e => setGroupName(e.target.value)}
                                placeholder={tab === 'channel' ? 'например, hr-объявления' : 'например, Проект Альфа'}
                            />
                        </div>
                    )}

                    {error && (
                        <div
                            style={{
                                padding: '10px 12px',
                                borderRadius: 'var(--radius-md)',
                                background: 'var(--color-danger-bg)'
                            }}
                            className='flex items-center gap-8 mb-12 color-danger text-md fw-600'>{error}</div>
                    )}

                    <div className="form-field">
                        <label className="form-label">
                            {tab === 'direct' ? 'Сотрудник' : 'Участники (отметьте галочкой)'}
                        </label>
                        <div className="relative">
                            <Search
                                size={16}
                                style={{
                                    left: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)'
                                }}
                                className='absolute' />
                            <input
                                className="form-input"
                                placeholder="Имя или логин..."
                                value={userQuery}
                                onChange={e => setUserQuery(e.target.value)}
                                style={{ paddingLeft: 36 }}
                            />
                        </div>
                    </div>
                    <UserList
                        users={userHits}
                        searching={searching}
                        tab={tab}
                        selectedIds={selectedUserIds}
                        onPickDirect={handleStartDirect}
                        onToggle={toggleSelectUser}
                    />
                    {tab !== 'direct' && selectedUserIds.size > 0 && (
                        <div className="text-rel-sm text-muted mt-8">
                            Выбрано: <strong className="color-text">{selectedUserIds.size}</strong>
                        </div>
                    )}
                    {(tab === 'group' || tab === 'channel') && (
                        <button
                            type="button"
                            className="save-btn mt-16"
                           
                            disabled={!groupName.trim()}
                            onClick={() => void submitGroupOrChannel()}
                        >Создать</button>
                    )}
                    <button type="button" className="btn btn--secondary mt-12 w-full" onClick={onClose}>
                        <X size={14} /> Отмена
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * UserList — список найденных сотрудников для выбора в чат / группу / канал.
 *
 * Card-row с initials-аватаром (стабильный hue из user.id), ФИО, бейджем роли
 * справа и чекбоксом (для group/channel) или просто click-стартом (для direct).
 * Заменил предыдущий plain-button без `.btn`/padding/avatar/checkbox, который
 * рендерился гигантским center-aligned текстом без иерархии.
 */
function UserList({ users, searching, tab, selectedIds, onPickDirect, onToggle }) {
    const isMulti = tab !== 'direct'

    const rows = useMemo(() => users || [], [users])

    if (searching) {
        return (
            <div className="card text-center" style={{ padding: '24px 16px', marginTop: 12 }}>
                <div className="text-rel-sm text-muted">Поиск…</div>
            </div>
        )
    }

    if (rows.length === 0) {
        return (
            <div className="card text-center" style={{ padding: '24px 16px', marginTop: 12 }}>
                <div className="text-rel-sm text-muted">
                    Никого не нашёл. Попробуй другое имя или логин.
                </div>
            </div>
        )
    }

    return (
        <div
            style={{
                maxHeight: 320,
                marginTop: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                paddingRight: 4,
            }}
            className="overflow-auto"
        >
            {rows.map((u) => {
                const selected = isMulti && selectedIds.has(String(u.id))
                const hue = avatarHue(u.id)
                return (
                    <button
                        key={u.id}
                        type="button"
                        onClick={() => (isMulti ? onToggle(u.id) : onPickDirect(u))}
                        aria-pressed={isMulti ? selected : undefined}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            width: '100%',
                            padding: '8px 10px',
                            background: selected
                                ? 'var(--color-accent-bg)'
                                : 'var(--color-bg-elevated)',
                            border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`,
                            borderRadius: 12,
                            textAlign: 'left',
                            cursor: 'pointer',
                            transition: 'background 120ms ease, border-color 120ms ease',
                            font: 'inherit',
                            fontSize: 13,
                            lineHeight: 1.3,
                        }}
                    >
                        {/* Аватар: инициалы на цветном фоне */}
                        <span
                            aria-hidden="true"
                            style={{
                                flex: '0 0 auto',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `hsl(${hue}, 60%, 92%)`,
                                color: `hsl(${hue}, 55%, 30%)`,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                            }}
                        >
                            {initialsOf(u.full_name)}
                        </span>

                        {/* Имя + роль */}
                        <span style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <span
                                style={{
                                    display: 'block',
                                    fontWeight: 600,
                                    color: 'var(--color-text)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {displayName(u.full_name)}
                            </span>
                            <span
                                style={{
                                    display: 'block',
                                    fontSize: 11,
                                    color: 'var(--color-text-muted)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {roleLabel(u.role)}
                            </span>
                        </span>

                        {/* Чекбокс (для group/channel) — визуальный, состояние из selected */}
                        {isMulti && (
                            <span
                                aria-hidden="true"
                                style={{
                                    flex: '0 0 auto',
                                    width: 20,
                                    height: 20,
                                    borderRadius: 6,
                                    border: `1.5px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`,
                                    background: selected ? 'var(--color-accent)' : 'transparent',
                                    color: 'var(--color-text-inverse)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 120ms ease, border-color 120ms ease',
                                }}
                            >
                                {selected ? <Check size={13} strokeWidth={3} /> : null}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}
